/* eslint-disable react/react-in-jsx-scope */
import { useEffect, useState } from "react";
import { Text, Flex, VStack, HStack, Spinner, Button, Box, Input, Image, useDisclosure } from "@chakra-ui/react";
import { AuthService } from "services/authService";
import { ArrowForwardIcon } from "@chakra-ui/icons";
import { useWelcome } from "./hooks";
import { useTranslation } from "react-i18next";
import GenderPicker from "common/GenderPicker";
import SelectDatePickerCmp from "common/SelectDatePickerCmp";
import { AgeType, Child, Gender, UserRole } from "data-models";
import { QUESTION_ACTIONS, useFollowUp } from "providers/FollowUpProvider";
import { useHistory, useLocation } from "react-router-dom";
import { AgeSelector } from "./FollowUp/components";
import { HelpToolTip } from "./FollowUp/HelpToolTip";
import { BsInfoCircleFill } from "react-icons/bs";
import PreschoolDropdown from "common/PreschoolDropdown";
import DepartmentDropdown from "common/DepartmentDropdown";
import FollowupDropdown from "common/FollowupDropdown";
import WelcomeInstructionIcon from "../../assets/images/WelcomeInstruction-Icon.svg";
import InstructionModal from "./InstructionModal";
import { useUser } from "providers/UserProvider";
import HelpdeskComponent from "common/Helpdesk";

export const welcomeInstructionsTitle = "Att läsa innan ni genomför Myran:";

export default function Welcome() {
  const { setChild, dispatch, originalTasks } = useFollowUp();
  const {
    handleNext,
    loading,
    onPreschoolSelect,
    FollowUpOptions,
    preschoolOptions,
    preschool,
    goToChildInformation,
    selectedFollowupId,
    setSelectedFollowupId,
    handleSelectDepartment,
    departmentId,
    organization,
  } = useWelcome();
  const { replace } = useHistory();
  const { t, i18n } = useTranslation();
  const [gender, setGender] = useState(Gender.none);
  const [IsSelectedDate, setIsSelectedDate] = useState(false);
  const [birthdate, setBirthdate] = useState("");
  const [age, setAge] = useState<string>("");
  const [childCode, setChildCode] = useState<string>("");

  const logout = () => AuthService.logout();

  const {
    user: { role },
  } = useUser();

  const { isOpen, onOpen, onClose } = useDisclosure();
  const location = useLocation();

  const sortedPreschoolOptions = preschoolOptions.sort((a, b) => String(a.label).localeCompare(String(b.label), "sv"));

  const handleChangeAge = (newAge: string) => {
    setAge(newAge);
  };
  const handleChangeChildCode = e => {
    setChildCode(e.target.value);
  };

  const handleChangeId = (id: string) => {
    setSelectedFollowupId(id);
  };
  useEffect(() => {
    if (selectedFollowupId !== "") {
      goToChildInformation();
    }
  }, [selectedFollowupId]);

  function removeLanguageSessionStorageItems() {
    const keysToRemove: any = [];

    // Collect keys that need to be removed
    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i);
      if (key && key.endsWith("-language")) {
        keysToRemove.push(key);
      }
    }

    // Remove collected keys
    keysToRemove.forEach(key => sessionStorage.removeItem(key));
  }

  useEffect(() => {
    const rootDiv = document.getElementById("root");
    if (location.pathname === "/welcome") {
      if (rootDiv) rootDiv.classList.add("welcome-root"); // Add the class to the root div
    }

    removeLanguageSessionStorageItems();

    // Cleanup function to remove the class when the component unmounts or the URL changes
    return () => {
      if (rootDiv) rootDiv.classList.remove("welcome-root");
    };
  }, [location]);

  function navToTasks() {
    if (!isStartDisabled) {
      setChild({ gender, birthdate, age, code: childCode } as Child);
      return replace("/tasks");
    }
  }

  const isStartDisabled =
    !(IsSelectedDate || age) ||
    !selectedFollowupId ||
    !gender ||
    !departmentId ||
    ((organization?.showChildCode || role === UserRole.PEDAGOGUE) && !childCode);

  const isPedagogue = role === UserRole.PEDAGOGUE;
  const showAgeSelector = organization?.ageType !== AgeType.BIRTH_DATE;

  const isCancelDisabled =
    !(IsSelectedDate || age) &&
    !selectedFollowupId &&
    gender === "None" &&
    !departmentId &&
    (organization?.showChildCode || isPedagogue ? !childCode : true);

  const resetForm = e => {
    onPreschoolSelect({ label: "", value: "" });
    handleSelectDepartment({ label: "", value: "" });
    handleChangeId("");
    setGender(Gender.none);
    setIsSelectedDate(false);
    setBirthdate("");
    handleChangeAge("");
    handleChangeChildCode(e);
  };

  const radioGroupStyles = {
    ".chakra-radio__label": {
      fontSize: "13px",
      fontWeight: 500,
    },
    ".chakra-radio__control": {
      borderColor: "pink.DEFAULT",
      _checked: {
        bg: "pink.DEFAULT",
        borderColor: "pink.DEFAULT",
        _before: { bg: "#fff" },
      },
      _hover: { borderColor: "pink.DEFAULT" },
      _focus: { boxShadow: "none" },
    },
  };

  return (
    <Box className="welcome-outer-wrapper">
      <Flex
        direction="column"
        height="600px"
        width="640px"
        className="welcome-wrapper"
        margin="0 auto"
        padding="0"
        justifyContent="space-evenly"
      >
        <HStack justifyContent="space-between">
          <Text fontSize={"24px"}>{t("Create_Followup")}</Text>
          <HelpdeskComponent />
        </HStack>
        <HStack flexWrap="wrap" justifyContent="space-between">
          <Flex>
            <Box mt={{ base: 4 }}>
              <Flex direction="column" alignItems="flex-start" color="black">
                <Box mb={2}>
                  <label htmlFor="preschool">{t("Preschool_Select_Box") + "  *"}</label>
                </Box>
                <PreschoolDropdown
                  key={i18n.language}
                  onPreschoolSelect={onPreschoolSelect}
                  preschoolOptions={sortedPreschoolOptions}
                  preschool={preschool}
                  placeHolder={t("Preschool_Select_Box")}
                  departmentId={departmentId}
                />
              </Flex>
            </Box>
          </Flex>
          <Flex>
            <Box mt={{ base: 4 }} ml={{ base: 4, lg: 2 }}>
              {organization?.hasMultipleSections && (
                <Flex direction="column" alignItems="flex-start">
                  <Box mb={2}>
                    <label htmlFor="department">{t("Department")} *</label>
                  </Box>
                  <DepartmentDropdown
                    key={i18n.language}
                    preschool={preschool}
                    placeHolder={t("Department")}
                    departmentId={departmentId}
                    handleNext={handleNext}
                    handleSelectDepartment={handleSelectDepartment}
                  />
                </Flex>
              )}
              {loading && <Spinner />}
            </Box>
          </Flex>
        </HStack>
        <Flex>
          <Box mr={{ base: 4 }} mt={{ base: "1rem" }}>
            <Flex direction="column" alignItems="flex-start">
              <Box mb={3}>
                <label htmlFor="followup">{t("Follow_Up_Select_Box") + "  *"}</label>
              </Box>
              <FollowupDropdown
                key={i18n.language}
                selectedFollowupId={selectedFollowupId}
                handleChangeId={handleChangeId}
                FollowUpOptions={FollowUpOptions}
                placeHolder={t("Follow_Up_Select_Box")}
              />
            </Flex>
          </Box>
        </Flex>
        <Flex>
          <Box>
            <Box mb={3}>
              <label htmlFor="GenderPicker">{t("Gender_Field") + "  *"}</label>
            </Box>
            <Box>
              <GenderPicker onValueChange={setGender} genderSelect={gender} />
            </Box>
          </Box>
        </Flex>
        <Flex>
          {!showAgeSelector ? (
            <Box width="100%">
              <VStack>
                <Flex direction="column" alignItems="flex-start" width="100%">
                  <Box mb={3}>
                    <label>{t("DOB_Field") + "  *"}</label>
                  </Box>
                  <SelectDatePickerCmp
                    IsSelectedChange={setIsSelectedDate}
                    onValueChange={setBirthdate}
                    value={birthdate}
                    key={i18n.language}
                  />
                </Flex>
              </VStack>
            </Box>
          ) : (
            <VStack
              minH="0"
              minW="0"
              //  w='full'
              justify="center"
              align="baseline"
              //pt='5'
              w="60%"
              //  pl='4'
            >
              <Text alignSelf="start" fontSize="sm">
                {t("Select_Age_Text") + "  *"}
              </Text>
              <Box className="age-selector-box">
                <AgeSelector age={age} onChange={handleChangeAge} />
              </Box>
            </VStack>
          )}
        </Flex>
        <Flex>
          {(organization?.showChildCode || role === UserRole.PEDAGOGUE) && (
            <VStack
              minH="0"
              minW="0"
              w="full"
              justify="center"
              align="center"
              pb="10"
              pt="3"

              // pt='5'
              //  pl='4'
            >
              <HStack w="full" align="center" justify="flex-start">
                <Text fontSize="sm">{t("Child_Alias_Field") + "  *"}</Text>
                <HelpToolTip
                  title={t("ResultsTable_Child_Alias")}
                  body={t("Child_Alias_Explanation")}
                  image={<BsInfoCircleFill />}
                />
              </HStack>
              <Input
                w="full"
                value={childCode}
                onChange={handleChangeChildCode}
                placeholder={`${t("Child_Alias_Placeholder")}...`}
                fontSize="13px"
                height="44px"
                className={childCode ? "" : "addError"}
              />
            </VStack>
          )}
        </Flex>
        <Flex justifySelf="end" width="640px" z-index="990">
          <Flex w="full" justifyContent="space-between">
            <HStack onClick={onOpen} cursor="pointer">
              <Image src={WelcomeInstructionIcon} />
              <Text fontSize="13px">{t("Welcome_Instruction_Text")}</Text>
            </HStack>
            <HStack>
              <Button
                fontWeight="bold"
                disabled={isCancelDisabled}
                onClick={e => resetForm(e)}
                height="40px"
                width="110px"
                variant="outline"
                bg=""
                color="pink.DEFAULT"
                borderColor="pink.DEFAULT"
                opacity={1}
                _hover={{
                  bg: "",
                  color: "pink.DEFAULT",
                  opacity: 1,
                }}
              >
                {t("Cancel_Button")}
              </Button>
              <Button
                fontWeight="bold"
                ml={5}
                rightIcon={<ArrowForwardIcon boxSize="5" />}
                onClick={navToTasks}
                disabled={isStartDisabled}
                colorScheme="pink"
                height="40px"
                width="110px"
                variant="outline"
                bg="pink.DEFAULT"
                color="white"
                borderColor="pink.DEFAULT"
                opacity={1}
                _hover={{
                  bg: "",
                  color: "white",
                  opacity: 1,
                }}
              >
                {t("Start_Button")}
              </Button>
            </HStack>
          </Flex>
        </Flex>
      </Flex>
      <InstructionModal isOpen={isOpen} onClose={onClose} />
    </Box>
  );
}
